import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert,
  Divider,
  Collapse,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutlined as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";

import {
  getContractAssignments,
  updateContractAssignments,
  getAvailableContracts,
} from "@accredo/fe-shared/services/contractAssignmentService.js";

/**
 * Formats a contract label from contract data.
 */
const formatContractLabel = (contract) => {
  const parts = [contract.contract_number, contract.contract_type].filter(
    Boolean
  );
  if (contract.company_name) {
    parts.push(`(${contract.company_name})`);
  }
  return parts.join(" – ");
};

/**
 * A single contract assignment row with scope toggle and object picker.
 */
function ContractAssignmentRow({
  assignment,
  onChange,
  onRemove,
  expanded,
  onToggleExpand,
}) {
  const handleScopeChange = (_event, new_scope) => {
    if (!new_scope) return;
    onChange({
      ...assignment,
      assignment_scope: new_scope,
      // When switching to "all", clear selected objects
      selected_object_ids:
        new_scope === "all" ? [] : assignment.selected_object_ids,
    });
  };

  const handleObjectToggle = (object_id) => {
    const current = assignment.selected_object_ids || [];
    const updated = current.includes(object_id)
      ? current.filter((id) => id !== object_id)
      : [...current, object_id];
    onChange({ ...assignment, selected_object_ids: updated });
  };

  const handleSelectAll = () => {
    const all_ids = (assignment.all_objects || []).map((obj) => obj.id);
    onChange({ ...assignment, selected_object_ids: all_ids });
  };

  const handleDeselectAll = () => {
    onChange({ ...assignment, selected_object_ids: [] });
  };

  const is_selected_scope = assignment.assignment_scope === "selected";
  const selected_count = (assignment.selected_object_ids || []).length;
  const total_count = assignment.total_objects_count || 0;
  const has_validation_error = is_selected_scope && selected_count === 0;

  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.5,
        }}
      >
        {/* Contract label */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" noWrap fontWeight={500}>
            {formatContractLabel(assignment)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {is_selected_scope
              ? `${selected_count} / ${total_count} Objekte ausgewählt`
              : `Alle ${total_count} Objekte`}
          </Typography>
        </Box>

        {/* Scope toggle */}
        <ToggleButtonGroup
          value={assignment.assignment_scope}
          exclusive
          onChange={handleScopeChange}
          size="small"
        >
          <ToggleButton value="all">Alle</ToggleButton>
          <ToggleButton value="selected">Objektauswahl</ToggleButton>
        </ToggleButtonGroup>

        {/* Expand/collapse for object picker */}
        {is_selected_scope && (
          <IconButton size="small" onClick={onToggleExpand}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}

        {/* Remove button */}
        <Tooltip title="Vertrag entfernen">
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Validation error */}
      {has_validation_error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          Bei Objektauswahl muss mindestens ein Objekt ausgewählt werden.
        </Alert>
      )}

      {/* Object picker */}
      <Collapse in={expanded && is_selected_scope}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={handleSelectAll}
              variant="outlined"
            >
              Alle auswählen
            </Button>
            <Button
              size="small"
              startIcon={<DeselectIcon />}
              onClick={handleDeselectAll}
              variant="outlined"
            >
              Alle abwählen
            </Button>
          </Box>
          <Box
            sx={{
              maxHeight: 300,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {(assignment.all_objects || []).map((obj) => {
              const is_checked = (
                assignment.selected_object_ids || []
              ).includes(obj.id);
              const label = obj.customer_reference_number
                ? `${obj.bezeichnung} (${obj.customer_reference_number})`
                : obj.bezeichnung;
              return (
                <FormControlLabel
                  key={obj.id}
                  control={
                    <Checkbox
                      checked={is_checked}
                      onChange={() => handleObjectToggle(obj.id)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap>
                      {label}
                    </Typography>
                  }
                  sx={{ ml: 0 }}
                />
              );
            })}
            {(!assignment.all_objects ||
              assignment.all_objects.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                Keine Objekte für diesen Vertrag vorhanden.
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

/**
 * Main panel for managing user-contract-object assignments.
 */
export default function ContractAssignmentPanel({ userId }) {
  const { enqueueSnackbar } = useSnackbar();

  const [assignments, setAssignments] = useState([]);
  const [initial_assignments, setInitialAssignments] = useState([]);
  const [available_contracts, setAvailableContracts] = useState([]);
  const [is_loading, setIsLoading] = useState(true);
  const [is_saving, setIsSaving] = useState(false);
  const [expanded_contracts, setExpandedContracts] = useState({});
  const [selected_new_contract, setSelectedNewContract] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [assignment_data, available_data] = await Promise.all([
        getContractAssignments(userId),
        getAvailableContracts(userId),
      ]);

      const loaded = (assignment_data?.data?.assignments || []).map((a) => ({
        ...a,
        selected_object_ids: a.selected_object_ids || [],
      }));

      setAssignments(loaded);
      setInitialAssignments(JSON.parse(JSON.stringify(loaded)));
      setAvailableContracts(available_data || []);
    } catch (error) {
      enqueueSnackbar("Zuordnungen konnten nicht geladen werden.", {
        variant: "error",
      });
      console.error("Failed to fetch contract assignments", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const is_dirty =
    JSON.stringify(assignments) !== JSON.stringify(initial_assignments);

  const has_validation_errors = assignments.some(
    (a) =>
      a.assignment_scope === "selected" &&
      (!a.selected_object_ids || a.selected_object_ids.length === 0)
  );

  const handleAssignmentChange = (index, updated) => {
    setAssignments((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  };

  const handleRemoveAssignment = (index) => {
    const removed = assignments[index];
    setAssignments((prev) => prev.filter((_, i) => i !== index));

    // Add back to available contracts
    if (removed) {
      setAvailableContracts((prev) => [
        ...prev,
        {
          contract_id: removed.contract_id,
          contract_number: removed.contract_number,
          contract_type: removed.contract_type,
          company_name: removed.company_name,
        },
      ]);
    }
  };

  const handleAddContract = () => {
    if (!selected_new_contract) return;

    const new_assignment = {
      contract_id: selected_new_contract.contract_id,
      contract_number: selected_new_contract.contract_number,
      contract_type: selected_new_contract.contract_type,
      company_name: selected_new_contract.company_name,
      assignment_scope: "all",
      selected_object_ids: [],
      all_objects: [],
      total_objects_count: 0,
    };

    setAssignments((prev) => [...prev, new_assignment]);
    setAvailableContracts((prev) =>
      prev.filter((c) => c.contract_id !== selected_new_contract.contract_id)
    );
    setSelectedNewContract(null);
  };

  const handleToggleExpand = (contract_id) => {
    setExpandedContracts((prev) => ({
      ...prev,
      [contract_id]: !prev[contract_id],
    }));
  };

  const handleSave = async () => {
    if (has_validation_errors) {
      enqueueSnackbar("Bitte korrigieren Sie die Fehler vor dem Speichern.", {
        variant: "warning",
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = assignments.map((a) => ({
        contract_id: a.contract_id,
        assignment_scope: a.assignment_scope,
        selected_object_ids:
          a.assignment_scope === "selected" ? a.selected_object_ids : [],
      }));

      await updateContractAssignments(userId, payload);
      enqueueSnackbar("Zuordnungen gespeichert.", { variant: "success" });

      // Refetch to get fresh data (including all_objects for newly added contracts)
      await fetchData();
    } catch (error) {
      enqueueSnackbar("Fehler beim Speichern der Zuordnungen.", {
        variant: "error",
      });
      console.error("Failed to save contract assignments", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (is_loading) {
    return <Spinner />;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Vertrags- / Objektzuordnungen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Verwalten Sie die Vertragszuordnungen und Objektsichtbarkeit für diesen
        Benutzer.
      </Typography>

      {/* Assignment rows */}
      {assignments.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Keine Vertragszuordnungen vorhanden. Fügen Sie einen Vertrag hinzu.
        </Alert>
      )}

      {assignments.map((assignment, index) => (
        <ContractAssignmentRow
          key={assignment.contract_id}
          assignment={assignment}
          onChange={(updated) => handleAssignmentChange(index, updated)}
          onRemove={() => handleRemoveAssignment(index)}
          expanded={!!expanded_contracts[assignment.contract_id]}
          onToggleExpand={() => handleToggleExpand(assignment.contract_id)}
        />
      ))}

      {/* Add contract section */}
      <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 2, alignItems: "center" }}>
        <Autocomplete
          value={selected_new_contract}
          onChange={(_event, value) => setSelectedNewContract(value)}
          options={available_contracts}
          getOptionLabel={(option) => formatContractLabel(option)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vertrag hinzufügen"
              size="small"
              placeholder="Vertrag suchen..."
            />
          )}
          sx={{ minWidth: 350 }}
          noOptionsText="Keine weiteren Verträge verfügbar"
          isOptionEqualToValue={(option, value) =>
            option.contract_id === value.contract_id
          }
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddContract}
          disabled={!selected_new_contract}
        >
          Hinzufügen
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Save button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {is_dirty && (
          <DsButton variant="tertiary" onClick={fetchData} disabled={is_saving}>
            Änderungen verwerfen
          </DsButton>
        )}
        <DsButton
          variant="primary"
          onClick={handleSave}
          disabled={!is_dirty || has_validation_errors || is_saving}
        >
          {is_saving ? "Speichert..." : "Speichern"}
        </DsButton>
      </Box>
    </Paper>
  );
}
